import type { Mode } from "./match.ts";
import { itemDef, type ItemDef, type WeaponStats } from "./items.ts";

/** Tipos de dano (doc 02 §11). Fraqueza: ×1,5 se *algum* tipo do ataque coincidir. */
export const DAMAGE_TYPES = ["cortante", "contundente", "perfurante", "fogo", "agua", "raio"] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];

/** Quem causou o dano: decide bônus de conjunto e, no futuro, estatísticas de morte (doc 06). */
export type DamageSource = "jogador" | "criatura" | "assombracao" | "chefe" | "fogo" | "ambiente";

/** Números do corpo a corpo (doc 02 §10–§11; doc 07, A7). Hipóteses de balanceamento. */
export const MELEE = {
  /** Ciclo na velocidade 1 (o catálogo define `speed` como golpes por ciclo-base). */
  baseCycle: 0.6,
  /** O golpe acerta a 35 % do ciclo. */
  impactFraction: 0.35,
  /** Segurar repete; pedir o próximo até 0,15 s antes do fim do ciclo entra no buffer. */
  buffer: 0.15,
  /** Cone a partir do olho: meia-abertura horizontal e vertical (cossenos de 45° e 35°). */
  cosHalfH: 0.7071067811865476,
  cosHalfV: 0.8191520442889918,
  sinHalfV: 0.573576436351046,
  /** Raio do corpo alvo somado ao alcance. */
  bodyRadius: 0.35,
  variance: [0.85, 1.15] as const,
  critChance: 0.08,
  critMul: 1.8,
  weakMul: 1.5,
  /** Repulsão quando o golpe passa desta fração da vida máxima do alvo (ou arma com `knockback`). */
  knockbackFraction: 0.2,
  knockbackSpeed: 6,
  knockbackLift: 3,
  /** Atraso de interpolação do cliente (ms): os outros corpos são vistos 100 ms no passado. */
  interpolationDelay: 100,
} as const;

/** Rebobinagem máxima da compensação de lag (doc 07, A7): 200 ms no Mutirão, 150 ms na Treta. */
export const maxRewindMs = (mode: Mode): number => (mode === "treta" ? 150 : 200);

/** Soco: o que se usa sem arma (e com o arco, no corpo a corpo, até a F15). */
export const FISTS: WeaponStats = { family: "clava", damage: 5, speed: 1.2, reach: 1.8, targets: 1, falloff: 1 };

/**
 * Se o item na mão golpeia: o que tem golpe (`weapon`: armas e ferramentas — machado e picareta batem
 * como clava) e a mão vazia, no soco. Material, comida, peça de construção, estação e o resto que não é
 * feito para bater não atacam.
 */
export function meleeAllowed(itemId: string | undefined): boolean {
  return !itemId || itemDef(itemId)?.weapon !== undefined;
}

/** Arma de corpo a corpo do item na mão: a do item ou o soco (quem pode golpear é `meleeAllowed`). */
export function meleeWeapon(itemId: string | undefined): WeaponStats {
  const weapon = itemId ? itemDef(itemId)?.weapon : undefined;
  return weapon && weapon.family !== "arco" ? weapon : FISTS;
}

/** Duração do ciclo de golpe, em segundos. */
export const meleeCycle = (weapon: WeaponStats): number => MELEE.baseCycle / weapon.speed;

const FAMILY_TYPE: Record<WeaponStats["family"], DamageType> = {
  clava: "contundente",
  lamina: "cortante",
  lanca: "perfurante",
  cajado: "raio",
  arco: "perfurante",
};

/** Tipos do golpe: o da família da arma, mais Raio nas armas de Pedra-de-raio (`vsBoss`). */
export function damageTypes(weapon: WeaponStats): DamageType[] {
  const types: DamageType[] = [FAMILY_TYPE[weapon.family]];
  if (weapon.vsBoss) types.push("raio");
  return types;
}

export type ArmorSet = NonNullable<NonNullable<ItemDef["armor"]>["set"]>;

export interface ArmorSummary {
  defense: number;
  /** Conjunto completo vestido (as 4 peças do mesmo conjunto), se houver. */
  set: ArmorSet | undefined;
}

/** Defesa somada das peças vestidas e o conjunto completo (doc 03, Armaduras). */
export function armorSummary(itemIds: readonly string[]): ArmorSummary {
  let defense = 0;
  const sets = new Set<string>();
  let pieces = 0;
  for (const id of itemIds) {
    const armor = id ? itemDef(id)?.armor : undefined;
    if (!armor) continue;
    defense += armor.defense;
    pieces++;
    sets.add(armor.set ?? "");
  }
  const only = sets.size === 1 ? [...sets][0] : "";
  return { defense, set: pieces === 4 && only ? (only as ArmorSet) : undefined };
}

/**
 * Bônus de conjunto completo (doc 03): Couro corre 15 % mais rápido; Prata reduz em 20 % o dano
 * de assombrações; Raio reduz em 60 % o dano de fogo e herda o bônus da Prata.
 */
export function setDamageFactor(set: ArmorSet | undefined, source: DamageSource): number {
  if (set === "prata" && source === "assombracao") return 0.8;
  if (set === "raio") return source === "fogo" ? 0.4 : source === "assombracao" ? 0.8 : 1;
  return 1;
}

export const setSprintMul = (set: ArmorSet | undefined): number => (set === "couro" ? 1.15 : 1);
